import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, CardTitle, Spinner, Alert
} from "reactstrap";
import ReactApexChart from "react-apexcharts";

import WelcomeComp from "./WelcomeComp";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import {
  getUsers as onGetUsers,
} from "store/contacts/actions";

import {
  getDashboardStats,
  getDashboardDailyActivity,
  getDashboardScoreBands,
  getDashboardQuestionTypeBreakdown,
} from "../../helpers/ai_api";

const Dashboard = (props) => {
  const dispatch = useDispatch();

  // Premium count için hâlâ Users (User_Subscription expand'i ile) gerek — daha iyi bir SQL view eklemek istenirse buradan kalkar
  const { users } = useSelector((s) => ({ users: s.contacts.users }));

  // ── Server-side aggregated state ──
  const [stats, setStats]       = useState(null);
  const [daily, setDaily]       = useState([]);
  const [bands, setBands]       = useState(null);
  const [types, setTypes]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    if (!users || !users.length) dispatch(onGetUsers());
  }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      getDashboardStats(),
      getDashboardDailyActivity(30),
      getDashboardScoreBands(),
      getDashboardQuestionTypeBreakdown(),
    ])
      .then(([s, d, b, t]) => {
        setStats(s); setDaily(d); setBands(b); setTypes(t);
      })
      .catch((e) => setError(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, []);

  // ── Premium hesabı Redux'tan (varsa) ──
  const premiumCount = useMemo(() => {
    if (!users || users.length === 0) return 0;
    const now = new Date();
    return users.filter((user) => {
      if (!user.User_Subscription?.length) return false;
      return user.User_Subscription.some((s) => {
        const end = new Date(s.End_Date);
        return s.IsActive === true && end > now;
      });
    }).length;
  }, [users]);

  // ── Stat kartları ──
  // NOT: API camelCase döndürür (WebApiConfig CamelCasePropertyNamesContractResolver).
  const totalUsers     = stats?.totalUsers ?? 0;
  const newUsers7      = stats?.newUsers7d ?? 0;
  const activeUsers7   = stats?.activeUsers7d ?? 0;
  const totalSim       = stats?.totalSimReports ?? 0;
  const totalPractice  = stats?.totalPracticeReports ?? 0;
  const totalAnswered  = stats?.totalAnsweredQuestions ?? 0;
  const avgScore       = stats?.avgScore ? Math.round(stats.avgScore) : 0;

  const cards = [
    { title: "Toplam Kullanıcı", value: totalUsers,    icon: "bx-user",        color: "primary" },
    { title: "Premium Üye",       value: premiumCount,  icon: "bx-crown",       color: "info"    },
    { title: "Aktif Son 7 Gün",   value: activeUsers7,  icon: "bx-pulse",       color: "success" },
    { title: "Yeni Kayıt (7g)",   value: newUsers7,     icon: "bx-user-plus",   color: "warning" },

    { title: "Toplam Sınav",       value: totalSim,      icon: "bx-file",         color: "primary" },
    { title: "Toplam Pratik",      value: totalPractice, icon: "bx-edit",         color: "success" },
    { title: "Çözülen Soru",       value: totalAnswered, icon: "bx-check-circle", color: "info"    },
    { title: "Free Üye",            value: totalUsers - premiumCount, icon: "bx-user-circle", color: "secondary" },
  ];

  // ── Daily chart ──
  const dailyChart = useMemo(() => {
    const days  = (daily || []).map(d => (d.date || "").slice(0, 10));
    const counts = (daily || []).map(d => d.count || 0);
    return {
      series: [{ name: "Rapor", data: counts }],
      options: {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        stroke: { curve: "smooth", width: 3 },
        dataLabels: { enabled: false },
        xaxis: { categories: days, labels: { rotate: -45, style: { fontSize: "10px" } } },
        colors: ["#556ee6"],
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.0 } },
      },
    };
  }, [daily]);

  // ── Type pie ──
  const typeChart = useMemo(() => {
    const entries = (types || []).filter(t => (t.count || 0) > 0);
    return {
      series: entries.map(t => t.count),
      options: {
        chart: { type: "donut" },
        labels: entries.map(t => t.type || "Diğer"),
        colors: ["#556ee6", "#34c38f", "#f1b44c", "#74788d", "#f46a6a"],
        legend: { position: "bottom" },
        plotOptions: { pie: { donut: { size: "70%" } } },
      },
    };
  }, [types]);

  // ── Score bands bar ──
  const bandsChart = useMemo(() => {
    const data = bands ? [bands.band_0_39, bands.band_40_59, bands.band_60_79, bands.band_80_100] : [0, 0, 0, 0];
    return {
      series: [{ name: "Rapor", data }],
      options: {
        chart: { toolbar: { show: false } },
        xaxis: { categories: ["0-39", "40-59", "60-79", "80-100"] },
        plotOptions: { bar: { borderRadius: 4, distributed: true } },
        colors: ["#f46a6a", "#f1b44c", "#50a5f1", "#34c38f"],
        dataLabels: { enabled: true },
        legend: { show: false },
      },
    };
  }, [bands]);

  document.title = "Anasayfa | DLAvia Yönetim Paneli";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={props.t("Yönetim")} breadcrumbItem={props.t("Kontrol Paneli")} />

          {error && <Alert color="warning">İstatistik yüklenemedi: {error}</Alert>}
          {loading && (
            <div className="text-center py-3">
              <Spinner size="sm" /> <span className="ms-2">İstatistikler yükleniyor…</span>
            </div>
          )}

          {/* ── Welcome + üst stat sırası ── */}
          <Row>
            <Col xl="4"><WelcomeComp /></Col>
            <Col xl="8">
              <Row>
                {cards.slice(0, 4).map((c, i) => (
                  <Col md="6" key={i}><StatCard {...c} /></Col>
                ))}
              </Row>
            </Col>
          </Row>

          {/* ── Alt stat sırası ── */}
          <Row>
            {cards.slice(4).map((c, i) => (
              <Col md="3" sm="6" key={i}><StatCard {...c} /></Col>
            ))}
          </Row>

          {/* ── Özet metrikler ── */}
          <Row>
            <Col md="4">
              <Card><CardBody className="text-center">
                <h6 className="text-muted">Ortalama Skor</h6>
                <h2 className={`text-${avgScore >= 60 ? "success" : avgScore >= 40 ? "warning" : "danger"}`}>
                  {avgScore}
                </h2>
              </CardBody></Card>
            </Col>
            <Col md="4">
              <Card><CardBody className="text-center">
                <h6 className="text-muted">Free / Premium Oranı</h6>
                <h4>
                  <span className="text-muted">{totalUsers - premiumCount}</span>
                  {" / "}
                  <span className="text-info">{premiumCount}</span>
                </h4>
                <small className="text-muted">
                  %{totalUsers ? Math.round((premiumCount / totalUsers) * 100) : 0} premium
                </small>
              </CardBody></Card>
            </Col>
            <Col md="4">
              <Card><CardBody className="text-center">
                <h6 className="text-muted">Sınav / Pratik Oranı</h6>
                <h4>
                  <span className="text-primary">{totalSim}</span>
                  {" / "}
                  <span className="text-success">{totalPractice}</span>
                </h4>
              </CardBody></Card>
            </Col>
          </Row>

          {/* ── Grafikler ── */}
          <Row>
            <Col lg="8">
              <Card><CardBody>
                <CardTitle className="mb-4">Günlük Aktivite (Son 30 Gün)</CardTitle>
                {dailyChart.series[0].data.length > 0 ? (
                  <ReactApexChart options={dailyChart.options} series={dailyChart.series} type="area" height={300} />
                ) : (
                  <p className="text-muted text-center py-5">Veri yok</p>
                )}
              </CardBody></Card>
            </Col>
            <Col lg="4">
              <Card><CardBody>
                <CardTitle className="mb-4">Soru Türü Dağılımı</CardTitle>
                {typeChart.series.length > 0 ? (
                  <ReactApexChart options={typeChart.options} series={typeChart.series} type="donut" height={300} />
                ) : (
                  <p className="text-muted text-center py-5">Veri yok</p>
                )}
              </CardBody></Card>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card><CardBody>
                <CardTitle className="mb-4">Skor Dağılımı</CardTitle>
                <ReactApexChart options={bandsChart.options} series={bandsChart.series} type="bar" height={250} />
              </CardBody></Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

function StatCard({ title, value, icon, color }) {
  return (
    <Card className="mini-stats-wid">
      <CardBody>
        <div className="d-flex">
          <div className="flex-grow-1">
            <p className="text-muted fw-medium">{title}</p>
            <h4 className="mb-0">{value}</h4>
          </div>
          <div className={`avatar-sm rounded-circle bg-${color} align-self-center mini-stat-icon`}>
            <span className={`avatar-title rounded-circle bg-${color}`}>
              <i className={"bx " + icon + " font-size-24"}></i>
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

Dashboard.propTypes = { t: PropTypes.any };

export default withTranslation()(Dashboard);
